import React, { useEffect, useMemo, useState } from 'react';
import { CarLoader } from '../../components/loader';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Typography, Tag, Space, Button, Form, Input, Select, DatePicker, InputNumber, Spin } from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined, DollarOutlined, CalendarOutlined, FileTextOutlined, CreditCardOutlined, UserOutlined
} from '@ant-design/icons';
import { feeActions } from '../../actions/feeActions';
import { categoryActions } from '../../actions/categoryActions';
import { commonActions } from '../../actions/commonActions';
import { userActions } from '../../actions/userActions';
import dayjs from 'dayjs';
import GuardedButton from '../../components/guardedButton';
import { useCurrentPageAccess } from '../../services/helper';

const { Text } = Typography;
const { TextArea } = Input;

interface FeeViewProps {
  role?: string;
}

const paymentModeOptions = [
  { label: 'Cash', value: 'CASH' },
  { label: 'UPI', value: 'UPI' },
  { label: 'Card', value: 'CARD' },
  { label: 'Net Banking', value: 'NET_BANKING' },
  { label: 'Cheque', value: 'CHEQUE' },
  { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
];

const getPaymentModeColor = (mode: string) => {
  const colorMap: Record<string, string> = {
    'CASH': 'green',
    'UPI': 'blue',
    'CARD': 'purple',
    'NET_BANKING': 'cyan',
    'CHEQUE': 'orange',
    'BANK_TRANSFER': 'geekblue',
  };
  return colorMap[mode] || 'default';
};

const FeeView: React.FC<FeeViewProps> = ({ role }) => {

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const feeIdFromState = location.state?.feeId;
  const [form] = Form.useForm();
  const pageAccess = useCurrentPageAccess();

  const [isEditMode, setIsEditMode] = useState(false);
  const [fee, setFee] = useState<any>(null);

  const feeView = useSelector((state: any) => state.getFeeViewReducer);
  const feeData = useSelector((state: any) => state.createFeeReducer);
  const categoryData = useSelector((state: any) => state.getCategoryParamsReducer);
  const serviceData = useSelector((state: any) => state.getServiceParamsReducer);
  const studentData = useSelector((state: any) => state.userListReducer);
  const roleListData = useSelector((state: any) => state.roleDataReducer);

  const categoryOptions = useMemo(() => {
    const payload = categoryData?.categorydata;
    const list = payload?.categories ?? payload ?? [];
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      label: c?.categoryName,
      value: c?._id,
    }));
  }, [categoryData]);

  const serviceOptions = useMemo(() => {
    const payload = serviceData?.servicedata;
    const list = payload?.services ?? payload ?? [];
    return (Array.isArray(list) ? list : []).map((s: any) => ({
      label: s?.serviceName,
      value: s?._id,
    }));
  }, [serviceData]);

  const studentOptions = useMemo(() => {
    const users = studentData?.userList?.users ?? [];
    return (Array.isArray(users) ? users : []).map((s: any) => {
      const name = `${s?.firstName || ''} ${s?.lastName || ''}`.trim();
      const studentId = s?.enrolledNumber || s?.studentId;
      const displayLabel = studentId ? `${name} (${studentId})` : name;

      return {
        label: displayLabel,
        value: s?._id,
        enrolledNumber: studentId
      };
    });
  }, [studentData]);

  useEffect(() => {
    if (feeIdFromState) {
      dispatch(feeActions.getFeeView(feeIdFromState));
      dispatch(categoryActions.getCategoryParams({ isActive: true }));
      dispatch(commonActions.getServiceParams({ pageNumber: 1, pageSize: 100 }));
      dispatch(userActions.userRoleRequest());
    }
  }, [feeIdFromState, dispatch]);

  useEffect(() => {
    if (roleListData && roleListData.roleData) {
      dispatch(userActions.userListRequest({ role: roleListData?.roleData?.find((r: any) => r.roleName === 'Student')?._id }));
    }
  }, [roleListData, dispatch]);

  useEffect(() => {
    if (feeView?.isSuccess && feeView?.feeview) {
      setFee(feeView.feeview);
      feeView.isSuccess = false;
    }
  }, [feeView]);

  useEffect(() => {
    if (feeData?.isSuccess) {
      setIsEditMode(false);
      dispatch(feeActions.getFeeView(feeIdFromState));
      feeData.isSuccess = false;
    }
  }, [feeData, feeIdFromState, dispatch]);

  const handleEdit = () => {
    if (fee) {
      form.setFieldsValue({
        category: fee.category?._id,
        service: fee.service?._id,
        student: fee.student?._id,
        totalFee: fee.totalFee,
        paidAmount: fee.paidAmount,
        pendingAmount: fee.pendingAmount,
        paymentDate: fee.paymentDate ? dayjs(fee.paymentDate) : null,
        paymentMode: fee.paymentMode,
        remarks: fee.remarks,
      });
      setIsEditMode(true);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.resetFields();
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      const payload = {
        _id: fee?._id,
        categoryId: values.category || fee?.category?._id,
        serviceId: values.service || fee?.service?._id,
        studentId: values.student || fee?.student?._id,
        totalFee: values.totalFee,
        paidAmount: values.paidAmount,
        pendingAmount: values.pendingAmount,
        paymentDate: values.paymentDate ? values.paymentDate.format('YYYY-MM-DD') : fee?.paymentDate,
        paymentMode: values.paymentMode,
        remarks: values.remarks || '',
      };
      dispatch(feeActions.createFee(payload));
    });
  };

  if (feeView?.isLoading) {
    return (
      <div className='view-loading'>
        <CarLoader />
      </div>
    );
  }

  if (!fee) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/accounting')}>Back</Button>
        <Card style={{ marginTop: 16 }}>
          <Text type="secondary">Fee record not found</Text>
        </Card>
      </div>
    );
  }

  const studentName = fee.student ? `${fee.student.firstName || ''} ${fee.student.lastName || ''}`.trim() : '-';
  const canEdit = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;
  const createdLabel = fee.createdAt ? dayjs(fee.createdAt).format('DD MMM YYYY') : null;
  const summary = [
    { label: 'Total Fee', value: fee.totalFee, color: '#1890ff' },
    { label: 'Paid', value: fee.paidAmount, color: '#52c41a' },
    { label: 'Pending', value: fee.pendingAmount, color: fee.pendingAmount > 0 ? '#fa8c16' : '#52c41a' },
  ];

  return (
    <div className="batch-view-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/accounting', { state: { from: 'feeView' } })} className="batch-back-btn">Accounting</Button>
        <div className="batch-header-right">
          {createdLabel && <span className="batch-created">Created {createdLabel}</span>}
          {!isEditMode ? (
            canEdit && (
              <GuardedButton className="batch-edit-btn" icon={<EditOutlined />} onClick={handleEdit}>Edit Fee</GuardedButton>
            )
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancel}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={feeData?.isLoading}>Save</Button>
            </Space>
          )}
        </div>
      </div>

      {/* Title / hero */}
      <div className="batch-hero">
        <div className="batch-hero-icon"><DollarOutlined /></div>
        <div className="batch-hero-info">
          <div className="batch-hero-titlerow">
            <h2 className="batch-hero-name">{studentName}</h2>
            <Tag color={getPaymentModeColor(fee.paymentMode)} style={{ borderRadius: 999, margin: 0 }}>
              <CreditCardOutlined style={{ marginRight: 4 }} />
              {fee.paymentMode?.replace('_', ' ') || '-'}
            </Tag>
          </div>
          <div className="batch-hero-meta">
            {fee.category?.categoryName && <span className="batch-hero-meta-item"><FileTextOutlined /> {fee.category.categoryName}</span>}
            {fee.paymentDate && <span className="batch-hero-meta-item"><CalendarOutlined /> {dayjs(fee.paymentDate).format('DD MMM YYYY')}</span>}
          </div>
        </div>
      </div>

      {/* Fee summary strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {summary.map((s) => (
          <div key={s.label} style={{ flex: 1, minWidth: 130, padding: '14px 16px', background: '#f9fafb', border: '1px solid #eef0f3', borderRadius: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af' }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, marginTop: 4 }}>₹{s.value?.toLocaleString('en-IN') || 0}</div>
          </div>
        ))}
      </div>

      {/* Body */}
      <Form form={form} layout="vertical" className="batch-view-form">
        {isEditMode ? (
          <Card className="entity-details-card fees-entry-view" bordered={false}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item name="student" label="Student">
                  <Select options={studentOptions} placeholder="Select student" showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="totalFee" label="Total Fee" rules={[{ required: true, message: 'Please enter total fee' }]}>
                  <InputNumber prefix="₹" style={{ width: '100%' }} min={0} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="paidAmount" label="Paid Amount" rules={[{ required: true, message: 'Please enter paid amount' }]}>
                  <InputNumber prefix="₹" style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="pendingAmount" label="Pending Amount">
                  <InputNumber prefix="₹" style={{ width: '100%' }} min={0} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="category" label="Category">
                  <Select options={categoryOptions} placeholder="Select category" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="service" label="Service">
                  <Select options={serviceOptions} placeholder="Select service" disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true, message: 'Please select payment date' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="paymentMode" label="Payment Mode" rules={[{ required: true, message: 'Please select payment mode' }]}>
                  <Select options={paymentModeOptions} placeholder="Select payment mode" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <TextArea rows={3} placeholder="Enter remarks" maxLength={500} showCount />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        ) : (
          <div className="batch-details-pane">
            <div className="batch-section-label">Properties</div>
            <div className="batch-props">
              <div className="batch-prop-row">
                <div className="batch-prop-key"><UserOutlined className="batch-prop-ic" /> Student</div>
                <div className="batch-prop-val">{studentName}</div>
              </div>
              <div className="batch-prop-row">
                <div className="batch-prop-key"><FileTextOutlined className="batch-prop-ic" /> Category</div>
                <div className="batch-prop-val">
                  {fee.category?.categoryName ? <Tag color="blue">{fee.category.categoryName}</Tag> : <span className="batch-prop-muted">-</span>}
                </div>
              </div>
              <div className="batch-prop-row">
                <div className="batch-prop-key"><FileTextOutlined className="batch-prop-ic" /> Service</div>
                <div className="batch-prop-val">
                  {fee.service?.serviceName ? <Tag color="purple">{fee.service.serviceName}</Tag> : <span className="batch-prop-muted">-</span>}
                </div>
              </div>
              <div className="batch-prop-row">
                <div className="batch-prop-key"><CreditCardOutlined className="batch-prop-ic" /> Payment Mode</div>
                <div className="batch-prop-val">
                  <Tag color={getPaymentModeColor(fee.paymentMode)}>{fee.paymentMode?.replace('_', ' ') || '-'}</Tag>
                </div>
              </div>
              <div className="batch-prop-row">
                <div className="batch-prop-key"><CalendarOutlined className="batch-prop-ic" /> Payment Date</div>
                <div className="batch-prop-val">{fee.paymentDate ? dayjs(fee.paymentDate).format('DD MMM YYYY') : '-'}</div>
              </div>
              <div className="batch-prop-row">
                <div className="batch-prop-key"><CalendarOutlined className="batch-prop-ic" /> Created</div>
                <div className="batch-prop-val">{fee.createdAt ? dayjs(fee.createdAt).format('DD MMM YYYY, hh:mm A') : '-'}</div>
              </div>
              {fee.updatedAt && (
                <div className="batch-prop-row">
                  <div className="batch-prop-key"><CalendarOutlined className="batch-prop-ic" /> Updated</div>
                  <div className="batch-prop-val">{dayjs(fee.updatedAt).format('DD MMM YYYY, hh:mm A')}</div>
                </div>
              )}
            </div>

            <div className="batch-section-label" style={{ marginTop: 26 }}>Remarks</div>
            <div className="batch-textblock">{fee.remarks || <span className="batch-prop-muted">No remarks</span>}</div>
          </div>
        )}
      </Form>
    </div>
  );
};

export default FeeView;
